import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getAuthToken, handleGetLocal } from "../../services/auth-service";
import { Popup } from "devextreme-react/popup";
import { Download, MoreHorizontal, Search } from "lucide-react";
import "./SupervisionTestResults.css";
import ExcelJS from "exceljs";

const columns = [
  { key: "participantId", label: "Participant Id" },
  { key: "altId", label: "Alt Id" },
  { key: "lastName", label: "Last Name" },
  { key: "firstName", label: "First Name" },
  { key: "agency", label: "Agency" },
  { key: "supervisor", label: "Supervisor" },
  { key: "testDate", label: "Test Date" },
  { key: "resultDate", label: "Result Date" },
  { key: "testType", label: "Test Type" },
  { key: "testResult", label: "Test Result" },
];

const pad = (number) => String(number).padStart(2, "0");

const toDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const initialDateRange = () => {
  const now = new Date();

  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    startDate: toDateKey(start),
    endDate: toDateKey(end),
  };
};

const displayDate = (value) => {
  if (!value) return "—";

  const valueString = String(value);

  const match = valueString.match(/^(\d{4})-(\d{2})-(\d{2})/);

  return match ? `${match[2]}/${match[3]}/${match[1]}` : valueString;
};

const csvCell = (value) => {
  let text = String(value ?? "");

  if (/^\s*[=+\-@]/.test(text)) {
    text = `'${text}`;
  }

  return `"${text.replace(/"/g, '""')}"`;
};

const saveExcel = async (rows) => {
  const workbook = new ExcelJS.Workbook();

  workbook.creator = "Pronto";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet("Test Results");

  // Use the same columns displayed in the grid
  worksheet.columns = columns.map((col) => ({
    header: col.label,
    key: col.key,
    width:
      col.key === "agency" || col.key === "supervisor" || col.key === "testType" || col.key === "testResult" ? 28 : 18,
  }));

  // Header styling
  const headerRow = worksheet.getRow(1);

  headerRow.font = {
    bold: true,
    color: { argb: "FF203956" },
  };

  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEEF3F9" },
  };

  headerRow.height = 26;

  headerRow.alignment = {
    vertical: "middle",
  };

  // Prevent untrusted text from being treated as an Excel formula
  const safeValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    const text = String(value);

    return /^\s*[=+\-@]/.test(text) ? `'${text}` : text;
  };

  // Add every filtered record, not just the current page
  rows.forEach((item) => {
    const rowData = {};

    columns.forEach((col) => {
      const value =
        col.key === "testDate" || col.key === "resultDate"
          ? item[col.key]
            ? displayDate(item[col.key])
            : ""
          : item[col.key];

      rowData[col.key] = safeValue(value);
    });

    const row = worksheet.addRow(rowData);

    row.alignment = {
      vertical: "middle",
      wrapText: true,
    };
  });

  // Enable Excel column filtering
  worksheet.autoFilter = {
    from: {
      row: 1,
      column: 1,
    },
    to: {
      row: 1,
      column: columns.length,
    },
  };

  // Freeze the header row
  worksheet.views = [
    {
      state: "frozen",
      ySplit: 1,
    },
  ];

  // Download XLSX file
  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = "SupervisionTestResults.xlsx";

  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const saveCsv = (rows) => {
  const header = columns.map((col) => csvCell(col.label)).join(",");

  const lines = rows.map((row) =>
    columns
      .map((col) => {
        const value = col.key === "testDate" || col.key === "resultDate" ? displayDate(row[col.key]) : row[col.key];

        return csvCell(value);
      })
      .join(","),
  );

  const blob = new Blob(["\uFEFF", header, "\r\n", lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = "SupervisionTestResults.csv";

  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function SupervisionTestResults() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [draftRange, setDraftRange] = useState(initialDateRange);
  const [activeRange, setActiveRange] = useState(initialDateRange);
  const [dateError, setDateError] = useState("");

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});

  const [sort, setSort] = useState({
    key: "testDate",
    direction: "desc",
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [menuId, setMenuId] = useState(null);
  const [selected, setSelected] = useState(null);

  // Keep an object available for the Popup even when closed.
  const detail = selected ?? {};

  const closePopup = () => {
    setSelected(null);
    setMenuId(null);
  };

  const openDetailPopup = (item) => {
    setMenuId(null);
    setSelected(item);
  };

  // --------------------------------------------------
  // Load Test Results from API
  // --------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    const fetchTestResults = async () => {
      setLoading(true);
      setError("");
      setResults([]);

      try {
        const token = getAuthToken("token");
        const domain = handleGetLocal("pathURL");

        if (!token || !domain) {
          throw new Error("Authentication information is missing.");
        }

        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/DrugTest/supervision-test-results`, {
          params: {
            startDate: activeRange.startDate,
            endDate: activeRange.endDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            Domain: domain,
          },
        });

        if (cancelled) return;

        if (response.data?.isSuccess !== true) {
          throw new Error(response.data?.message || "Unable to retrieve drug test results.");
        }

        const records = Array.isArray(response.data?.data) ? response.data.data : [];

        const mappedResults = records.map((item) => ({
          ...item,

          testDate: item.testDate ? String(item.testDate).slice(0, 10) : "",

          resultDate: item.resultDate ? String(item.resultDate).slice(0, 10) : "",
        }));

        setResults(mappedResults);
      } catch (err) {
        if (cancelled) return;

        console.error("Supervision Test Results API error:", err);

        setError(err.response?.data?.message || err.message || "Unable to load drug test results.");

        setResults([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTestResults();

    return () => {
      cancelled = true;
    };
  }, [activeRange]);

  // --------------------------------------------------
  // Search, Filter, Sort
  // --------------------------------------------------

  const filteredResults = useMemo(() => {
    const term = search.trim().toLowerCase();

    return results
      .filter((item) => {
        const dateKey = String(item.testDate ?? "").slice(0, 10);

        if (!dateKey || dateKey < activeRange.startDate || dateKey > activeRange.endDate) {
          return false;
        }

        const normalized = (key) =>
          String(key === "testDate" || key === "resultDate" ? displayDate(item[key]) : (item[key] ?? "")).toLowerCase();

        if (term && !columns.some((col) => normalized(col.key).includes(term))) {
          return false;
        }

        return columns.every((col) =>
          normalized(col.key).includes(
            String(filters[col.key] ?? "")
              .trim()
              .toLowerCase(),
          ),
        );
      })
      .sort((a, b) => {
        const left = String(a[sort.key] ?? "");
        const right = String(b[sort.key] ?? "");

        const comparison = left.localeCompare(right, undefined, {
          numeric: true,
          sensitivity: "base",
        });

        return sort.direction === "asc" ? comparison : -comparison;
      });
  }, [results, activeRange, filters, search, sort]);

  // --------------------------------------------------
  // Pagination
  // --------------------------------------------------

  const pageCount = Math.max(1, Math.ceil(filteredResults.length / pageSize));

  const currentPage = Math.min(page, pageCount);

  const visibleResults = filteredResults.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // --------------------------------------------------
  // Apply Dates
  // --------------------------------------------------

  const applyDates = () => {
    if (!draftRange.startDate || !draftRange.endDate) {
      setDateError("Select both start and end dates.");
      return;
    }

    if (draftRange.startDate > draftRange.endDate) {
      setDateError("Start Date cannot be after End Date.");
      return;
    }

    setDateError("");
    setActiveRange({ ...draftRange });
    setPage(1);
  };

  // --------------------------------------------------
  // Sorting
  // --------------------------------------------------

  const toggleSort = (key) => {
    setSort((previous) => ({
      key,
      direction: previous.key === key && previous.direction === "asc" ? "desc" : "asc",
    }));

    setPage(1);
  };

  return (
    <div className="str-page">
      {/* PAGE HEADING */}

      <div className="str-heading">
        <h2>Drug Test Results</h2>
        <p>Records available under your Pronto permissions.</p>
      </div>

      {/* DATE FILTERS */}

      <div className="str-date-bar">
        <div className="str-date-field">
          <label htmlFor="str-start-date">Start Date</label>

          <input
            id="str-start-date"
            type="date"
            value={draftRange.startDate}
            onChange={(e) =>
              setDraftRange((prev) => ({
                ...prev,
                startDate: e.target.value,
              }))
            }
          />
        </div>

        <div className="str-date-field">
          <label htmlFor="str-end-date">End Date</label>

          <input
            id="str-end-date"
            type="date"
            value={draftRange.endDate}
            onChange={(e) =>
              setDraftRange((prev) => ({
                ...prev,
                endDate: e.target.value,
              }))
            }
          />
        </div>

        <button type="button" className="str-btn" onClick={applyDates}>
          Apply Dates
        </button>

        <span className="str-date-help">Filter by Test Date</span>
      </div>

      {dateError && (
        <p className="str-error" role="alert">
          {dateError}
        </p>
      )}

      {/* RESULTS CARD */}

      <div className="str-card">
        {/* TOOLBAR */}

        <div className="str-toolbar">
          <span className="str-toolbar-title">Test Results</span>

          <div className="str-export-actions">
            <button
              type="button"
              className="str-btn"
              disabled={loading}
              onClick={async () => {
                try {
                  await saveExcel(filteredResults);
                } catch (error) {
                  console.error("Excel export failed:", error);
                  setError("Unable to export test results to Excel.");
                }
              }}
            >
              <Download size={14} />
              Export to Excel
            </button>

            <button type="button" className="str-btn" onClick={() => saveCsv(filteredResults)}>
              <Download size={14} />
              Export to CSV
            </button>
          </div>

          <div className="str-search">
            <Search size={16} />

            <input
              aria-label="Search test results"
              placeholder="Search test results..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {/* LOADING / ERROR */}

        {loading && <p className="str-loading">Loading drug test results...</p>}

        {error && (
          <p className="str-error" role="alert">
            {error}
          </p>
        )}

        {/* DATA TABLE */}

        <div className="str-table-scroll">
          <table className="str-table">
            <thead>
              <tr>
                <th>Actions</th>

                {columns.map((col) => (
                  <th key={col.key} className="str-sortable" onClick={() => toggleSort(col.key)}>
                    {col.label}

                    <span>{sort.key === col.key ? (sort.direction === "asc" ? " ↑" : " ↓") : " ↕"}</span>
                  </th>
                ))}
              </tr>

              <tr className="str-filter-row">
                <th />

                {columns.map((col) => (
                  <th key={col.key}>
                    <input
                      aria-label={`Filter ${col.label}`}
                      placeholder="Filter..."
                      value={filters[col.key] ?? ""}
                      onChange={(e) => {
                        setFilters((prev) => ({
                          ...prev,
                          [col.key]: e.target.value,
                        }));

                        setPage(1);
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {visibleResults.map((item) => (
                <tr key={item.drugTestId}>
                  {/* ACTIONS */}

                  <td>
                    <div className="str-action-wrap">
                      <button
                        type="button"
                        className="str-action-button"
                        aria-label={`Actions for ${item.participantId}`}
                        onClick={() => setMenuId(menuId === item.drugTestId ? null : item.drugTestId)}
                      >
                        <MoreHorizontal size={17} />
                      </button>

                      {menuId === item.drugTestId && (
                        <div className="str-action-menu">
                          <button type="button" onClick={() => openDetailPopup(item)}>
                            View Detail
                          </button>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* TABLE COLUMNS */}

                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.key === "participantId" ? (
                        <button type="button" className="str-id-link" onClick={() => openDetailPopup(item)}>
                          {item.participantId}
                        </button>
                      ) : col.key === "testDate" || col.key === "resultDate" ? (
                        displayDate(item[col.key])
                      ) : (
                        item[col.key] || "—"
                      )}
                    </td>
                  ))}
                </tr>
              ))}

              {!loading && !error && visibleResults.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="str-empty">
                    No test results found for these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="str-footer">
          <div className="str-page-sizes">
            {[10, 20, 30, 50, 100].map((size) => (
              <button
                key={size}
                type="button"
                className={`str-page-size-btn ${pageSize === size ? "active" : ""}`}
                onClick={() => {
                  setPageSize(size);
                  setPage(1);
                }}
              >
                {size}
              </button>
            ))}
          </div>

          <div className="str-pagination-right">
            <span className="str-page-summary">
              Page {currentPage} of {pageCount} ({filteredResults.length} items)
            </span>

            <div className="str-page-controls">
              <button
                type="button"
                className="str-page-arrow"
                disabled={currentPage === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                aria-label="Previous page"
              >
                ‹
              </button>

              <button
                type="button"
                className="str-page-number active"
                aria-label={`Current page ${currentPage}`}
                aria-current="page"
              >
                {currentPage}
              </button>

              <button
                type="button"
                className="str-page-arrow"
                disabled={currentPage === pageCount}
                onClick={() => setPage((prev) => Math.min(pageCount, prev + 1))}
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* DEVEXTREME VIEW DETAIL POPUP */}

      <Popup
        visible={selected !== null}
        onHiding={closePopup}
        showTitle={false}
        showCloseButton={false}
        hideOnOutsideClick={true}
        dragEnabled={false}
        width="min(660px, 95vw)"
        height={620}
        maxHeight="85vh"
        shading={true}
        shadingColor="rgba(30, 47, 70, 0.56)"
      >
        <div className="str-view-detail-popup">
          {/* POPUP HEADER */}

          <div className="str-view-detail-header">
            <div>
              <h3>Test Result Details</h3>

              <p>
                {[detail.firstName, detail.lastName].filter(Boolean).join(" ")}

                {detail.participantId ? ` · ${detail.participantId}` : ""}
              </p>
            </div>

            <button type="button" className="str-btn" onClick={closePopup}>
              Close
            </button>
          </div>

          <div className="str-view-detail-divider" />

          {/* POPUP DETAILS */}

          <div className="str-view-detail-grid">
            <div className="str-view-detail-field">
              <label>Participant Id</label>
              <p>{detail.participantId || "—"}</p>
            </div>

            <div className="str-view-detail-field">
              <label>Alt Id</label>
              <p>{detail.altId || "—"}</p>
            </div>

            <div className="str-view-detail-field">
              <label>Last Name</label>
              <p>{detail.lastName || "—"}</p>
            </div>

            <div className="str-view-detail-field">
              <label>First Name</label>
              <p>{detail.firstName || "—"}</p>
            </div>

            <div className="str-view-detail-field">
              <label>Agency</label>
              <p>{detail.agency || "—"}</p>
            </div>

            <div className="str-view-detail-field">
              <label>Supervisor</label>
              <p>{detail.supervisor || "—"}</p>
            </div>

            <div className="str-view-detail-field">
              <label>Test Date</label>
              <p>{displayDate(detail.testDate)}</p>
            </div>

            <div className="str-view-detail-field">
              <label>Result Date</label>
              <p>{displayDate(detail.resultDate)}</p>
            </div>

            <div className="str-view-detail-field">
              <label>Test Type</label>
              <p>{detail.testType || "—"}</p>
            </div>

            <div className="str-view-detail-field">
              <label>Test Result</label>
              <p>{detail.testResult || "—"}</p>
            </div>
            <div className="str-view-detail-field">
              <label>Test Reference</label>
              <p>{detail.testReference || "—"}</p>
            </div>

            <div className="str-view-detail-field">
              <label>Laboratory Status</label>
              <p>{detail.laboratoryStatus || "—"}</p>
            </div>

            <div className="str-view-detail-field str-view-detail-full">
              <label>Notes</label>
              <p>{detail.notes || "—"}</p>
            </div>
          </div>

          {/* POPUP FOOTER */}

          <div className="str-view-detail-footer">Read-only test result information.</div>
        </div>
      </Popup>
    </div>
  );
}
